import { useEffect, useMemo, useState } from 'react'
import type { FirebaseServices } from '../firebase'
import {
  listenToActions,
  listenToGame,
  listenToMember,
  listenToPlayers,
  readGameFromServer,
  readPlayersFromServer,
  readableFirebaseError,
} from '../gameService'
import type { Game, GameAction, Member, Player } from '../types'

const SERVER_RECONCILE_INTERVAL_MS = 4_000

function timestampIsBefore(left: Game['updatedAt'], right: Game['updatedAt']): boolean {
  if (!left || !right) return false
  return left.seconds < right.seconds || (left.seconds === right.seconds && left.nanoseconds < right.nanoseconds)
}

function newestPlayerTimestamp(players: Player[]): Player['updatedAt'] {
  return players.reduce<Player['updatedAt']>((newest, player) => {
    const candidate = player.updatedAt ?? player.joinedAt
    return !newest || (candidate && timestampIsBefore(newest, candidate)) ? candidate : newest
  }, null)
}

export function useLiveGame(services: FirebaseServices, code: string) {
  const [game, setGame] = useState<Game | null>(null)
  const [member, setMember] = useState<Member | null>(null)
  const [players, setPlayers] = useState<Player[]>([])
  const [actions, setActions] = useState<GameAction[]>([])
  const [error, setError] = useState<Error | null>(null)
  const [fromCache, setFromCache] = useState(true)
  const [actionsFromCache, setActionsFromCache] = useState(true)
  const [loaded, setLoaded] = useState({ game: false, member: false, players: false, actions: false })

  useEffect(() => {
    let active = true
    let reconcileInFlight = false
    let reconcileQueued = false
    let lastReconciledActionId: string | null = null
    let latestGame: Game | null = null
    let latestPlayers: Player[] = []
    const fail = (reason: Error) => setError(reason)
    const acceptGame = (value: Game | null, cached: boolean) => {
      setLoaded((current) => ({ ...current, game: true }))
      if (cached && !value && latestGame) return
      if (cached && value && !value.updatedAt && latestGame?.updatedAt) return
      if (value && latestGame && timestampIsBefore(value.updatedAt, latestGame.updatedAt)) return
      latestGame = value
      setGame(value)
      setFromCache(cached)
    }
    const acceptPlayers = (value: Player[], cached: boolean) => {
      setLoaded((current) => ({ ...current, players: true }))
      const valueTimestamp = newestPlayerTimestamp(value)
      const latestTimestamp = newestPlayerTimestamp(latestPlayers)
      if (cached && value.length < latestPlayers.length) return
      if (cached && !valueTimestamp && latestTimestamp) return
      if (timestampIsBefore(valueTimestamp, latestTimestamp)) return
      latestPlayers = value
      setPlayers(value)
    }
    const unsubscribes = [
      listenToGame(services.db, code, (value, cached) => {
        acceptGame(value, cached)
      }, fail),
      listenToMember(services.db, code, services.user.uid, (value) => {
        setMember(value)
        setLoaded((current) => ({ ...current, member: true }))
      }, fail),
      listenToPlayers(services.db, code, (value, cached) => {
        acceptPlayers(value, cached)
      }, fail),
      listenToActions(services.db, code, (value, cached) => {
        setActions(value)
        setActionsFromCache(cached)
        setLoaded((current) => ({ ...current, actions: true }))
      }, fail),
    ]

    const reconcileFromServer = async () => {
      if (!active || document.hidden || !navigator.onLine) return
      if (reconcileInFlight) {
        reconcileQueued = true
        return
      }

      reconcileInFlight = true
      try {
        const serverGame = await readGameFromServer(services.db, code)
        if (!active) return

        // The room's lastActionId changes in every state-changing transaction.
        // Only pay for the player query when a server revision has not yet been
        // reconciled by this watchdog (including its first pass).
        if (serverGame?.lastActionId !== lastReconciledActionId) {
          const serverPlayers = await readPlayersFromServer(services.db, code)
          if (!active) return
          acceptPlayers(serverPlayers, false)
          lastReconciledActionId = serverGame?.lastActionId ?? null
        }

        acceptGame(serverGame, false)
      } catch {
        // The realtime listeners remain authoritative and already surface their
        // own errors. A failed watchdog read usually just means the browser is
        // offline, so keep rendering the listener/cache state and retry later.
      } finally {
        reconcileInFlight = false
        if (active && reconcileQueued) {
          reconcileQueued = false
          void reconcileFromServer()
        }
      }
    }

    const reconcileWhenVisible = () => {
      if (!document.hidden) void reconcileFromServer()
    }
    const reconcileTimer = window.setInterval(() => void reconcileFromServer(), SERVER_RECONCILE_INTERVAL_MS)
    document.addEventListener('visibilitychange', reconcileWhenVisible)
    window.addEventListener('focus', reconcileWhenVisible)
    window.addEventListener('online', reconcileWhenVisible)
    window.addEventListener('pageshow', reconcileWhenVisible)
    void reconcileFromServer()

    return () => {
      active = false
      window.clearInterval(reconcileTimer)
      document.removeEventListener('visibilitychange', reconcileWhenVisible)
      window.removeEventListener('focus', reconcileWhenVisible)
      window.removeEventListener('online', reconcileWhenVisible)
      window.removeEventListener('pageshow', reconcileWhenVisible)
      unsubscribes.forEach((unsubscribe) => unsubscribe())
    }
  }, [code, services])

  const loading = useMemo(() => !Object.values(loaded).every(Boolean), [loaded])
  return { game, member, players, actions, error: error ? readableFirebaseError(error) : null, loading, fromCache, actionsFromCache }
}
